import {
  Idea,
  Feature,
  Requirement,
  Epic,
  Release,
  Initiative,
  Goal,
  PdlcStep,
  RecordLink,
  Project,
  Workflow,
  WorkflowKind,
  WorkflowKindTemplate,
} from 'components/framework/models';
import { MutationBatcher } from 'components/framework/MutationBatcher';
import {
  type CreateableRecords,
  type CreateRecordData,
  type IRecordManager,
} from 'javascripts/shared-types';
import { RecordLinkTypeEnum } from 'components/framework/Enums';

const GQL_BATCH_TIMEOUT = 200;
const GQL_BATCH_LIMIT = 20;

type CanCreatePermissions = Partial<Record<CreateableRecords, boolean>>;

const RecordManagerFactory = (
  getPageId: () => RecordId | undefined,
  canCreateOnly?: CreateableRecords
): IRecordManager => {
  const currentAccount = window?.currentAccount;
  let canCreate: CanCreatePermissions = {};

  const currentUser = window?.currentUser;

  if (!currentAccount || !currentUser || currentUser?.reviewerOnly) {
    canCreate = {};
  } else if (canCreateOnly) {
    canCreate = { [canCreateOnly]: true };
  } else {
    canCreate = {
      features:
        currentAccount.roadmapFlavor || currentAccount.teamworkOrDevelopFlavor,
      requirements:
        currentAccount.roadmapFlavor || currentAccount.teamworkOrDevelopFlavor,
      epics:
        currentAccount.roadmapFlavor || currentAccount.teamworkOrDevelopFlavor,
      releases: currentAccount.roadmapFlavor,
      initiatives: currentAccount.roadmapFlavor,
      goals: currentAccount.roadmapFlavor,
      ideas: currentAccount.roadmapFlavor || currentAccount.ideasFlavor,
      integrationRecords: currentAccount.whiteboardsAdvanced,
    };
  }

  const createRecordLinksToWhiteboard = async (records, type) => {
    const pageId = getPageId();

    if (!pageId) {
      return;
    }

    const batcher = await new MutationBatcher({
      timeout: GQL_BATCH_TIMEOUT,
      limit: GQL_BATCH_LIMIT,
    });

    await Promise.all(
      records.map(record =>
        record.save({
          args: { skipRequiredFieldsValidation: true },
          batcher,
        })
      )
    );

    await Promise.all(
      records.map(async record => {
        if (!record.id) {
          return Promise.resolve();
        }

        const recordLink = new RecordLink({
          linkType: RecordLinkTypeEnum.RELATES_TO, // To make the record dirty otherwise .save will do nothing
        });

        const attributes = {
          linkType: RecordLinkTypeEnum.RELATES_TO,
          parentRecord: {
            id: record.id,
            type,
          },
          childRecord: {
            id: pageId,
            type: 'Page',
          },
        };

        await recordLink.save({
          // Redefining the attributes as args because parentRecord and childRecord do not have the same structure for
          // RecordLinkType.parentRecord (union type) and RecordLinkAttributes.parentRecord ({ id: string, type: string })
          args: { attributes },
          batcher,
        });
      })
    );
  };

  const createRecords = async (
    records,
    type,
    args: any = { skipRequiredFieldsValidation: true }
  ) => {
    const batcher = new MutationBatcher({
      timeout: GQL_BATCH_TIMEOUT,
      limit: GQL_BATCH_LIMIT,
    });

    await Promise.all(
      records.map(record =>
        record.save({
          args,
          batcher,
        })
      )
    );

    await createRecordLinksToWhiteboard(records, type);
  };

  return {
    canCreateRecordType(recordType) {
      return canCreate[recordType] ?? false;
    },

    async createIdeas(ideas: CreateRecordData[]) {
      if (!canCreate.ideas) return;
      const records = ideas.map(
        ({ name, projectId, attributes }) =>
          new Idea({ ...attributes, name, project: { id: projectId } })
      );

      await createRecords(records, 'Idea');

      return ideas
        .map((idea, index) => ({ ...idea.meta, recordId: records[index]?.id }))
        .filter(({ recordId }) => recordId);
    },

    async createFeatures(features: CreateRecordData[]) {
      if (!canCreate.features) return;
      const records = features.map(
        ({ name, releaseId, attributes }) =>
          new Feature({
            ...attributes,
            name,
            release: { id: releaseId },
          })
      );

      await createRecords(records, 'Feature');

      return features
        .map((feature, index) => ({
          ...feature.meta,
          recordId: records[index]?.id,
        }))
        .filter(({ recordId }) => recordId);
    },

    async createRequirements(requirements: CreateRecordData[]) {
      if (!canCreate.requirements) return;
      const records = requirements.map(
        ({ name, featureId, attributes }) =>
          new Requirement({ ...attributes, name, feature: { id: featureId } })
      );

      await createRecords(records, 'Requirement');

      return requirements
        .map((requirement, index) => ({
          ...requirement.meta,
          recordId: records[index]?.id,
        }))
        .filter(({ recordId }) => recordId);
    },

    async createEpics(epics: CreateRecordData[]) {
      if (!canCreate.epics) return;
      const records = epics.map(
        ({ name, releaseId, attributes }) =>
          new Epic({ ...attributes, name, release: { id: releaseId } })
      );

      await createRecords(records, 'Epic');

      return epics
        .map((epic, index) => ({ ...epic.meta, recordId: records[index]?.id }))
        .filter(({ recordId }) => recordId);
    },

    async createReleases(releases: CreateRecordData[]) {
      if (!canCreate.releases) return;
      const records = releases.map(
        ({ name, projectId, attributes }) =>
          new Release({ ...attributes, name, project: { id: projectId } })
      );

      await createRecords(records, 'Release');

      return releases
        .map((release, index) => ({
          ...release.meta,
          recordId: records[index]?.id,
        }))
        .filter(({ recordId }) => recordId);
    },

    async createInitiatives(initiatives: CreateRecordData[]) {
      if (!canCreate.initiatives) return;
      const records = initiatives.map(
        ({ name, projectId, attributes }) =>
          new Initiative({ ...attributes, name, project: { id: projectId } })
      );

      await createRecords(records, 'Initiative');

      return initiatives
        .map((initiative, index) => ({
          ...initiative.meta,
          recordId: records[index]?.id,
        }))
        .filter(({ recordId }) => recordId);
    },

    async createGoals(goals: CreateRecordData[]) {
      if (!canCreate.goals) return;
      const records = goals.map(
        ({ name, projectId, attributes }) =>
          new Goal({ ...attributes, name, project: { id: projectId } })
      );

      await createRecords(records, 'Goal');

      return goals
        .map((goal, index) => ({ ...goal.meta, recordId: records[index]?.id }))
        .filter(({ recordId }) => recordId);
    },

    async createPdlcSteps(pdlcSteps: CreateRecordData[], { pdlcFrameworkId }) {
      if (!canCreate.pdlcSteps) return;
      const records = pdlcSteps.map(
        ({ name, attributes }) => new PdlcStep({ ...attributes, name })
      );

      await createRecords(records, 'PdlcStep', { pdlcFrameworkId });

      return pdlcSteps
        .map((step, index) => ({ ...step.meta, recordId: records[index]?.id }))
        .filter(({ recordId }) => recordId);
    },

    async getWorkflowKinds(releaseId: RecordId) {
      const result = await Release.select(['id', 'name'])
        .merge({
          project: Project.select(['id', 'name']).merge({
            featureWorkflow: Workflow.merge({
              workflowKinds: WorkflowKind.select([
                'id',
                'name',
                'position',
              ]).merge({
                featureTemplate: WorkflowKindTemplate.select(['id', 'body']),
              }),
            }),
          }),
        })
        .find(releaseId);

      return result.project.featureWorkflow.workflowKinds || [];
    },
  };
};

export default RecordManagerFactory;
